import ClassNames from "classnames";
import { FunctionComponent, MouseEvent } from "react";
import styles from "../stylesheets/components/main-menu.module.css";
import {
  ColorScheme,
  Language,
  MainMenuItem,
  PageState,
  TopHeaderVariant,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import MainMenuLevel from "./MainMenuLevel.js";

interface Props {
  menuItems: MainMenuItem[];
  parentMenuItem?: MainMenuItem;
  isPreview: boolean;
  pages: PageState;
  languageId: Language;
  scheme: ColorScheme | undefined;
  onMenuItemClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  menuLevel?: number;
  topHeaderVariant: TopHeaderVariant | undefined;
  activePagePath: string[];
  fallbackLanguageId: Language | undefined;
  lastItemRightAlign?: boolean;
  repeatFirstLevelItem: boolean;
}

const getListStyleClass = (scheme: ColorScheme, menuLevel: number) => {
  const style =
    menuLevel === 0
      ? { color: scheme.main.text }
      : {
          backgroundColor: scheme.primary.background,
          color: scheme.primary.text,
        };

  return css({
    ...style,
    "&::before": { borderBottomColor: scheme.primary.background },
  });
};

const MainMenuList: FunctionComponent<Props> = ({
  menuItems,
  parentMenuItem,
  isPreview,
  languageId,
  pages,
  scheme,
  onMenuItemClick,
  menuLevel = 0,
  topHeaderVariant,
  activePagePath,
  fallbackLanguageId,
  lastItemRightAlign,
  repeatFirstLevelItem,
}) => {
  const repeatParentItem = Boolean(
    repeatFirstLevelItem &&
    parentMenuItem &&
    !parentMenuItem.isFolder &&
    menuLevel === 1,
  );

  const listClassName = ClassNames(styles.MainMenu__List, {
    [styles["MainMenu__List--level0"]]: menuLevel === 0,
    [styles["MainMenu__List--level1"]]: menuLevel === 1,
    [styles["MainMenu__List--level2up"]]: menuLevel >= 2,
    [styles["MainMenu__List--last-right-align"]]: lastItemRightAlign,
    [styles["MainMenu__List--parent-item-repeated"]]: repeatParentItem,
  });

  if (!menuItems.length) return null;

  const combinedMenuItems = [
    ...(parentMenuItem && repeatParentItem ? [parentMenuItem] : []),
    ...menuItems,
  ];

  const listStyleClass = scheme && getListStyleClass(scheme, menuLevel);

  return (
    <ul className={ClassNames(listClassName, listStyleClass)}>
      {combinedMenuItems.map((currentMenuItem, index) => (
        <MainMenuLevel
          key={currentMenuItem.id}
          isExpanded={!repeatParentItem || index !== 0}
          currentMenuItem={currentMenuItem}
          isPreview={isPreview}
          languageId={languageId}
          parentId={currentMenuItem.id}
          pages={pages}
          scheme={scheme}
          onMenuItemClick={onMenuItemClick}
          menuLevel={menuLevel}
          topHeaderVariant={topHeaderVariant}
          activePagePath={activePagePath}
          fallbackLanguageId={fallbackLanguageId}
          index={index}
          repeatFirstLevelItem={repeatFirstLevelItem}
        />
      ))}
    </ul>
  );
};
export default MainMenuList;
